import {
  CssBaseline,
  ThemeProvider,
  Backdrop,
  CircularProgress,
  Alert,
} from "@mui/material";
import "./App.css";
import "react-data-grid/lib/styles.css";
import { useDarkMode, LAST_PATH_KEY } from "./common/contexts/GlobalContext";
import { lightTheme, darkTheme } from "./common/DetechtionTheme";
import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  getAllCodeLegends,
  getAllTagListNames,
  getAllUniversalDescriptors,
  getAvailablePermissions,
  getAvailableTenants,
  getLookupValues,
  getMasterCodeLegendEntries,
} from "./api/APIHelpers";
import { TagListContext } from "./common/contexts/TagListContext";
import { LookupValueContext } from "./common/contexts/LookupValueContext";
import { CodeLegendContext } from "./common/contexts/CodeLegendContext";
import { TenantContext } from "./common/contexts/TenantContext";
import ErrorBoundary from "./common/helpers/ErrorBoundary";
import { AlertsContext } from "./common/contexts/AlertsContext";
import { UniversalDescriptorContext } from "./common/contexts/UniversalDescriptorContext";

export default function App() {
  const darkModeState = useDarkMode();
  const location = useLocation();

  const [tagLists, setTagLists] = useState({ TagLists: [] });
  const [lookupValues, setLookupValues] = useState({});
  const [codeLegends, setCodeLegends] = useState({
    codeLegends: [],
    masterEntries: [],
  });
  const [universalDescriptors, setUniversalDescriptors] = useState([]);
  const [tenantInfo, setTenantInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [alertText, setAlertText] = useState("Missing Permission(s): ");
  const [showAlert, setShowAlert] = useState(false);
  const [infoMessages, setInfoMessages] = useState({
    type: "snackbar",
    message: "Loading...",
    severity: "info",
  });

  const refreshCodeLegendContext = useCallback(async () => {
    try {
      const codeLegendsResult = await getAllCodeLegends();
      const codeLegendDataList = codeLegendsResult.data.map((legend) => {
        return {
          id: legend.CodeLegendId,
          value: legend.Title,
          label: legend.Title,
        };
      });
      const masterEntriesResult = await getMasterCodeLegendEntries();
      const masterEntries = masterEntriesResult.data;
      setCodeLegends({
        codeLegends: codeLegendDataList,
        masterEntries: masterEntries,
      });
    } catch (error) {
      console.error("Failed to refresh code legends:", error);
    }
  }, []);

  const refreshUniversalDescriptorContext = useCallback(async () => {
    try {
      const universalDescriptorsResult = await getAllUniversalDescriptors();
      const udsOrderedByIsActive = universalDescriptorsResult.data.sort(
        (a, b) => {
          return b.IsActive - a.IsActive;
        }
      );
      setUniversalDescriptors(udsOrderedByIsActive);
    } catch (error) {
      console.error("Failed to refresh universal descriptors:", error);
    }
  }, []);

  const refreshTagListContext = useCallback(async () => {
    try {
      const tagListsResult = await getAllTagListNames();
      setTagLists({ TagLists: tagListsResult.data });
    } catch (error) {
      console.error("Failed to refresh tag lists:", error);
    }
  }, []);

  useEffect(() => {
    const excludePaths = ["/404", "/500"];
    if (!excludePaths.includes(location.pathname)) {
      localStorage.setItem(LAST_PATH_KEY, location.pathname);
    }
  }, [location]);

  useEffect(() => {
    const initializeApp = async () => {
      setLoading(true);
      try {
        // Load all data in parallel for better performance
        const [
          permissionsResult,
          tagListsResult,
          lookupValuesResult,
          codeLegendsResult,
          tenantsResult,
          masterEntriesResult,
          universalDescriptorsResult,
        ] = await Promise.allSettled([
          getAvailablePermissions(),
          getAllTagListNames(),
          getLookupValues(),
          getAllCodeLegends(),
          getAvailableTenants(),
          getMasterCodeLegendEntries(),
          getAllUniversalDescriptors(),
        ]);

        // Handle permissions
        if (permissionsResult.status === "fulfilled") {
          const result = permissionsResult.value;

          if (!result.data.permissions) {
            window.location.reload(true);
          }

          if (
            !result.data.permissions.find(
              (perm) => perm.PermissionName === "LookupValueRead"
            )
          ) {
            setAlertText((a) => a + "LookupValueRead ");
            setShowAlert(true);
          }
          if (
            !result.data.permissions.find(
              (perm) => perm.PermissionName === "CodeLegendRead"
            )
          ) {
            setAlertText((a) => a + "CodeLegendRead ");
            setShowAlert(true);
          }
          if (
            !result.data.permissions.find(
              (perm) => perm.PermissionName === "CanUpdateBulkTags"
            )
          ) {
            setAlertText((a) => a + "CanUpdateBulkTags ");
            setShowAlert(true);
          }
        }

        // Handle tag lists
        if (tagListsResult.status === "fulfilled") {
          setTagLists({ TagLists: tagListsResult.value.data });
        }

        // Handle lookup values
        if (lookupValuesResult.status === "fulfilled") {
          setLookupValues(lookupValuesResult.value.data);
        }

        // Handle universal descriptors
        if (universalDescriptorsResult.status === "fulfilled") {
          const udsOrderedByIsActive =
            universalDescriptorsResult.value.data.sort((a, b) => {
              return b.IsActive - a.IsActive;
            });
          setUniversalDescriptors(udsOrderedByIsActive);
        }

        // Handle code legends
        if (codeLegendsResult.status === "fulfilled") {
          let codeLegendDataList = codeLegendsResult.value.data.map(
            (legend) => {
              return {
                id: legend.CodeLegendId,
                value: legend.Title,
                label: legend.Title,
              };
            }
          );
          setCodeLegends((c) => ({ ...c, codeLegends: codeLegendDataList }));
        }

        if (masterEntriesResult.status === "fulfilled") {
          const masterEntries = masterEntriesResult.value.data;
          setCodeLegends((c) => ({ ...c, masterEntries: masterEntries }));
        }

        // Handle tenants
        if (tenantsResult.status === "fulfilled") {
          setTenantInfo(tenantsResult.value.data);
        } else {
          throw new Error(
            `Failed to load tenant data: ${tenantsResult.reason}`
          );
        }

        // Only stop loading if we have the essential data
        const hasEssentialData =
          lookupValuesResult.status === "fulfilled" &&
          tenantsResult.status === "fulfilled";

        if (hasEssentialData) {
          setLoading(false);
        } else {
          console.warn(
            "Some essential data failed to load, keeping loading state active"
          );
        }
      } catch (error) {
        console.error("Failed to initialize app:", error);
      }
    };

    initializeApp();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={darkModeState ? darkTheme : lightTheme}>
        <AlertsContext.Provider
          value={useMemo(
            () => ({ infoMessages, setInfoMessages }),
            [infoMessages, setInfoMessages]
          )}>
          <TagListContext.Provider
            value={useMemo(
              () => ({ ...tagLists, refreshTagListContext }),
              [tagLists, refreshTagListContext]
            )}>
            <UniversalDescriptorContext.Provider
              value={useMemo(
                () => ({
                  universalDescriptors,
                  refreshUniversalDescriptorContext,
                }),
                [universalDescriptors, refreshUniversalDescriptorContext]
              )}>
              <CodeLegendContext.Provider
                value={useMemo(
                  () => ({ ...codeLegends, refreshCodeLegendContext }),
                  [codeLegends, refreshCodeLegendContext]
                )}>
                <LookupValueContext.Provider value={lookupValues}>
                  <TenantContext.Provider value={tenantInfo}>
                    <Backdrop
                      open={loading}
                      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                      <CircularProgress />
                    </Backdrop>
                    <CssBaseline />
                    {showAlert && (
                      <Alert variant='filled' severity='error'>
                        {alertText}
                      </Alert>
                    )}
                    <Outlet />
                  </TenantContext.Provider>
                </LookupValueContext.Provider>
              </CodeLegendContext.Provider>
            </UniversalDescriptorContext.Provider>
          </TagListContext.Provider>
        </AlertsContext.Provider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
